/**
 * Team management API calls
 * Includes both team (formerly project) operations and user management
 */

import { apiClient } from './client';
import type { 
  TeamUser, 
  TeamUserResponse, 
  InviteUserRequest, 
  UpdateUserRequest, 
  ImpersonationRequest, 
  ImpersonationResponse,
  UserDetails,
  TeamResponse 
} from '@/types/team';

export interface LinkedInConnectionStatus {
  is_connected: boolean;
  has_account: boolean;
  premium_features?: string[];
}

export const teamApi = {
  // ============= Team (formerly Project) Operations =============

  /**
   * List teams for current user (backend uses impersonation/current user)
   */
  getTeams: async (params?: {
    page_token?: string;
    max_results?: number;
    search?: string; // maps to name filter server-side via DataProvider.teams(name=...)
    add_owner_info?: boolean; // include team owner information in response
    add_roles?: boolean; // include team roles with user information in response
    status?: string; // filter by status
    sort_field?: string; // sort field name (e.g. name, created_at, owner__name)
    sort_direction?: 'asc' | 'desc'; // sort direction
    role_name?: string; // filter teams where current user has this role via RoleUser
    owner_uuid?: string; // filter teams where this user is the owner
    manager_or_owner_uuid?: string; // filter teams where this user is owner or SERVICE_MANAGER (for backward compatibility)
    any_member_uuid?: string; // filter teams where this user has any role
    all_member_uuids?: string; // comma-separated list of user UUIDs - filter teams that contain ALL of these users (AND logic)
  }): Promise<TeamResponse> => {
    const response = await apiClient.get<TeamResponse>('/teams/', { params });
    return response.data;
  },

  /**
   * Create team (any authenticated user)
   */
  createTeam: async (data: {
    name: string;
    notes?: string;
    user_roles?: Array<{ user_uuid: string; role: 'SERVICE_MANAGER' | 'SERVICE_MEMBER' }>;
  }) => {
    const response = await apiClient.post<any>('/teams/', data);
    return response.data;
  },

  /**
   * Delete team - placeholder (implementation later)
   */
  deleteTeam: async (_teamUuid: string) => {
    throw new Error('Not implemented');
  },

  /**
   * Toggle team active status - placeholder (implementation later)
   */
  setTeamActive: async (_teamUuid: string, _active: boolean) => {
    throw new Error('Not implemented');
  },

  /**
   * Update team
   */
  updateTeam: async (teamUuid: string, data: any) => {
    const response = await apiClient.patch<any>(`/teams/${teamUuid}/update/`, data);
    return response.data;
  },

  /**
   * Unified prospect events endpoint
   */
  getProspectEvents: async (params: {
    team_uuid?: string;
    stream_uuid?: string;
    date_from?: string;
    date_to?: string;
    actions?: string[];
  }): Promise<Array<{ time: string; action: string; events: number }>> => {
    const response = await apiClient.get<{ output: Array<{ time: string; action: string; events: number }> }>(
      `/prospect-events/`,
      { params }
    );
    return response.data.output;
  },

  /**
   * Get team prospect events
   */
  getTeamProspectEvents: async (
    teamUuid: string,
    params: { date_from?: string; date_to?: string; actions?: string[] }
  ): Promise<Array<{ time: string; action: string; events: number }>> => {
    return teamApi.getProspectEvents({ ...params, team_uuid: teamUuid });
  },

  /**
   * Get stream prospect events
   */
  getStreamProspectEvents: async (
    streamUuid: string,
    params: { date_from?: string; date_to?: string; actions?: string[] }
  ): Promise<Array<{ time: string; action: string; events: number }>> => {
    return teamApi.getProspectEvents({ ...params, stream_uuid: streamUuid });
  },

  /**
   * Update stream by uuid
   */
  updateStream: async (streamUuid: string, data: any) => {
    const response = await apiClient.patch<any>(`/streams/${streamUuid}/update/`, data);
    return response.data;
  },

  /**
   * Get team roles
   */
  getTeamRoles: async (teamUuid: string): Promise<{
    output: {
      items: Array<{
        key: string;
        type: string;
        name: 'OWNER' | 'SERVICE_MANAGER' | 'SERVICE_MEMBER';
        user: {
          key: string;
          type: string;
          name: string | null;
          email: string | null;
          status?: string;
          profile_image?: string | null;
          is_staff?: boolean;
          is_superuser?: boolean;
          unipile_id?: string | null;
        };
        team: string;
        can_remove: boolean;
        created_at?: string;
      }>;
      next_page_token?: string;
      previous_page_token?: string;
      total_results?: number;
    };
  }> => {
    const response = await apiClient.get(`/teams/${teamUuid}/roles/`);
    return response.data;
  },

  /**
   * Add team role
   */
  addTeamRole: async (
    teamUuid: string,
    data: { user_uuid: string; role: 'SERVICE_MANAGER' | 'SERVICE_MEMBER' }
  ): Promise<{
    output: {
      key: string;
      type: string;
      name: 'SERVICE_MANAGER' | 'SERVICE_MEMBER';
      user: {
        key: string;
        type: string;
        name: string | null;
        email: string | null;
      };
      team: string;
      message: string;
      created_at?: string;
    };
  }> => {
    const response = await apiClient.post(`/teams/${teamUuid}/roles/add/`, data);
    return response.data;
  },

  /**
   * Remove team role (uses existing endpoint)
   */
  removeTeamRole: async (roleUuid: string): Promise<void> => {
    await apiClient.delete(`/users/roles/${roleUuid}/remove/`);
  },

  // ============= User Management Operations =============

  /**
   * Get list of users with pagination
   */
  getUsers: async (params?: {
    page_token?: string;
    max_results?: number;
    search?: string;
  }): Promise<TeamUserResponse> => {
    const response = await apiClient.get<TeamUserResponse>('/users/', { params });
    return response.data;
  },

  /**
   * Search across all users (requires staff, superuser, or service owner access)
   */
  searchAllUsers: async (params?: {
    page_token?: string;
    max_results?: number;
    search?: string;
  }): Promise<TeamUserResponse> => {
    const response = await apiClient.get<TeamUserResponse>('/users/search/', { params });
    return response.data;
  },

  /**
   * Get user details by UUID
   */
  getUserDetails: async (userUuid: string, addStatistics?: boolean): Promise<UserDetails> => {
    const params: Record<string, any> = {};
    if (addStatistics) {
      params['add_statistics'] = 'true';
    }
    const response = await apiClient.get<{ output: UserDetails }>(`/users/${userUuid}/`, { params });
    return response.data.output;
  },

  /**
   * Update user
   */
  updateUser: async (userUuid: string, data: UpdateUserRequest): Promise<TeamUser> => {
    const response = await apiClient.patch<{ output: TeamUser }>(`/users/${userUuid}/update/`, data);
    return response.data.output;
  },

  /**
   * Delete user
   */
  deleteUser: async (userUuid: string): Promise<void> => {
    await apiClient.delete(`/users/${userUuid}/delete/`);
  },

  /**
   * Invite new user
   */
  inviteUser: async (data: InviteUserRequest): Promise<TeamUser> => {
    const response = await apiClient.post<{ output: TeamUser }>('/users/invite/', data);
    return response.data.output;
  },

  /**
   * Start impersonation
   */
  startImpersonation: async (data: ImpersonationRequest): Promise<ImpersonationResponse> => {
    const response = await apiClient.post<ImpersonationResponse>('/impersonation/start/', data);
    return response.data;
  },

  /**
   * Stop impersonation
   */
  stopImpersonation: async (): Promise<ImpersonationResponse> => {
    const response = await apiClient.post<ImpersonationResponse>('/impersonation/stop/');
    return response.data;
  },

  /**
   * Get impersonation status
   */
  getImpersonationStatus: async (): Promise<ImpersonationResponse> => {
    const response = await apiClient.get<ImpersonationResponse>('/impersonation/status/');
    return response.data;
  },

  /**
   * Get LinkedIn connection status for a specific user
   */
  getUserLinkedInStatus: async (userUuid: string, forceFromSource?: boolean): Promise<LinkedInConnectionStatus> => {
    const params: Record<string, any> = { user_uuid: userUuid };
    if (forceFromSource) {
      params['force_from_source'] = true;
    }
    const response = await apiClient.get<{ output: LinkedInConnectionStatus }>('/user/linkedin/status/', {
      params,
    });
    return response.data.output;
  },

  /**
   * Remove user role (alias for removeTeamRole)
   */
  removeProjectRole: async (roleUuid: string): Promise<void> => {
    await apiClient.delete(`/users/roles/${roleUuid}/remove/`);
  },

  /**
   * Get pending team invites for current user
   */
  getPendingInvites: async (params?: {
    page_token?: string;
    max_results?: number;
  }): Promise<{
    output: {
      items: Array<{
        key: string;
        type: string;
        name: 'SERVICE_MANAGER' | 'SERVICE_MEMBER';
        status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
        user: string;
        project: {
          key: string;
          type: string;
          name: string;
        };
        team_name: string;
        created_at?: string;
      }>;
      next_page_token?: string;
      previous_page_token?: string;
      total_results?: number;
    };
  }> => {
    const response = await apiClient.get('/users/invites/pending/', { params });
    return response.data;
  },

  /**
   * Accept a team invite
   */
  acceptInvite: async (roleUuid: string): Promise<void> => {
    await apiClient.post(`/users/roles/${roleUuid}/accept/`);
  },

  /**
   * Reject a team invite
   */
  rejectInvite: async (roleUuid: string): Promise<void> => {
    await apiClient.post(`/users/roles/${roleUuid}/reject/`);
  },

  /**
   * Resend a rejected team invite (admin/service manager only)
   */
  resendInvite: async (roleUuid: string): Promise<void> => {
    await apiClient.post(`/users/roles/${roleUuid}/resend/`);
  },
};
